<template>
  <div class="recharge-page">
    <div class="recharge-shell">
      <header class="recharge-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="recharge-content">
        <section class="amount-card">
          <div class="field-head">
            <span>{{ copy.amountLabel }}</span>
            <small>{{ copy.minLabel }}</small>
          </div>

          <div class="amount-entry">
            <b>{{ copy.unit }}</b>
            <input
              v-model="money"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              :placeholder="copy.amountPlaceholder"
              @input="handleMoneyInput"
            />
          </div>

          <div class="balance-line">
            <span>{{ copy.balanceLabel }}</span>
            <b>{{ balanceText }} {{ copy.unit }}</b>
          </div>
        </section>

        <button
          type="button"
          class="submit-btn"
          :disabled="submitting"
          @click="submitRecharge"
        >
          {{ submitting ? copy.submittingText : copy.submitText }}
        </button>
      </main>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

const MIN_RECHARGE_AMOUNT = 1;

export default {
  data() {
    return {
      money: "",
      balance: "0.00",
      payWay: "Mexicopay",
      submitting: false,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "recharge");
    },
    balanceText() {
      const value = Number(this.balance);
      return Number.isFinite(value) ? value.toFixed(2) : "0.00";
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.balance = this.$route.params.balance || "0.00";
    this.getUserInfo();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async getUserInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_info",
      });
      if (res && res.code === 200 && res.data) {
        this.balance = res.data.money != null ? res.data.money : this.balance;
      }
    },
    handleMoneyInput(event) {
      const raw = (event && event.target && event.target.value) || this.money;
      const cleaned = raw.replace(/[^\d.]/g, "");
      const parts = cleaned.split(".");
      const integer = parts.shift() || "";
      const decimal = parts.join("").slice(0, 2);
      this.money = decimal ? `${integer}.${decimal}` : integer;
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    async submitRecharge() {
      if (this.submitting) {
        return;
      }
      if (!this.money) {
        this.$toast(this.copy.inputMoney);
        return;
      }
      const amount = Number(this.money);
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$toast(this.copy.submitError);
        return;
      }
      if (amount < MIN_RECHARGE_AMOUNT) {
        this.$toast(this.copy.minError);
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$http({
          method: "post",
          url: "recharge",
          data: {
            pay_way: this.payWay,
            money: amount.toFixed(2),
          },
        });
        if (res && res.code === 200) {
          this.$toast((res && res.msg) || this.copy.submitSuccess);
          this.money = "";
          return;
        }
        this.$toast((res && res.msg) || this.copy.submitError);
      } catch (error) {
        this.$toast(this.copy.submitError);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.recharge-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.recharge-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.recharge-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy {
  min-width: 0;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.recharge-content {
  padding: 1rem;
}

.amount-card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.045);
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
}

.field-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4375rem;
  color: #d5c8d2;
  font-size: 0.6875rem;
  font-weight: 800;
}

.field-head small {
  color: #a89baa;
  font-size: 0.5625rem;
  font-weight: 600;
}

.amount-entry {
  display: flex;
  align-items: center;
  gap: 0.5625rem;
  height: 4.0625rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
}

.amount-entry b {
  font-size: 0.9375rem;
  font-weight: 800;
}

.amount-entry input {
  min-width: 0;
  flex: 1;
  padding: 0;
  border: 0;
  background: transparent;
  color: #d8d0d6;
  font-size: 1.75rem;
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.025rem;
  outline: none;
}

.amount-entry input::placeholder {
  color: #8a8188;
}

.amount-entry input::-webkit-outer-spin-button,
.amount-entry input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.balance-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.625rem;
  color: #a89baa;
  font-size: 0.6875rem;
  font-weight: 600;
}

.balance-line b {
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 800;
}

.submit-btn {
  width: 100%;
  height: 3.125rem;
  margin-top: 0.875rem;
  border: 0;
  border-radius: 1.5625rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  box-shadow: 0 0.6875rem 1.625rem rgba(255, 63, 120, 0.24);
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 900;
}

.submit-btn[disabled] {
  opacity: 0.6;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .recharge-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
